import { useState } from 'react';
import { styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { ExternalRoutes, RoutePath } from '@constants/routes';
import Card from './Card';

const Container = styled(Paper)({
    padding: 24,
    '@media (max-width: 1024px)': {
        padding: 16,
    },
    marginBottom: 40,
    position: 'relative',
});

const StepsWrapper = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    marginBottom: 16,
    gridTemplateAreas: '"area0 area1 area2" "area3 area4 area5"',
    '@media (max-width: 1400px)': {
        gridGap: 16,
    },
    '@media (max-width: 1024px)': {
        gridTemplateAreas: '"area0 area3" "area1 area4" "area2 area5"',
    },
    '@media (max-width: 600px)': {
        gridTemplateAreas: '"area0" "area3" "area1" "area4" "area2" "area5"',
    },
});

const Onboarding = ({ account, accountOnboarding }) => {
    const isMobile = useMediaQuery('@media (max-width: 1024px)', { noSsr: true });
    const lsExpanded = localStorage.getItem('onboardingExpanded');
    const [expanded, setExpanded] = useState(lsExpanded ? JSON.parse(lsExpanded) : !isMobile);

    const { t, i18n } = useTranslation();

    const onExpandSection = () => {
        setExpanded(prev => {
            localStorage.setItem('onboardingExpanded', !prev);
            return !prev;
        });
    };

    const LIST = [
        {
            id: 0,
            title: t('HomePage.googleTitle'),
            description: t('HomePage.googleDescription'),
            isPassed: accountOnboarding.googleConnected,
            link: ExternalRoutes.LearnMore[i18n.language].google,
            internalLink: RoutePath.AccountSettingsIntegrations,
            style: { gridArea: 'area0' },
        },
        {
            id: 1,
            title: t('HomePage.channelsTitle'),
            description: t('HomePage.channelsDescription'),
            isPassed: accountOnboarding.channelsCreated,
            link: ExternalRoutes.LearnMore[i18n.language].channels,
            internalLink: RoutePath.AccountSettingsChannels,
            style: { gridArea: 'area1' },
        },
        {
            id: 2,
            title: t('HomePage.autoRepliesTitle'),
            description: t('HomePage.autoRepliesDescription'),
            isPassed: accountOnboarding.autoRepliesCreated,
            link: ExternalRoutes.LearnMore[i18n.language].autoReplies,
            internalLink: RoutePath.ReviewsAutomationAutoReplies,
            style: { gridArea: 'area2' },
        },
        {
            id: 3,
            title: t('HomePage.brandTitle'),
            description: t('HomePage.brandDescription'),
            isPassed: accountOnboarding.brandCreated,
            link: ExternalRoutes.LearnMore[i18n.language].brand,
            internalLink: RoutePath.AccountSettingsBrands,
            style: { gridArea: 'area3' },
        },
        {
            id: 4,
            title: t(account?.surveysEnabled === false ? 'HomePage.qrCodesTitle' : 'HomePage.templatesTitle'),
            description: t(
                account?.surveysEnabled === false ? 'HomePage.qrCodesDescription' : 'HomePage.templatesDescription',
            ),
            isPassed: accountOnboarding.surveyTemplatedCreated,
            link: ExternalRoutes.LearnMore[i18n.language].templates,
            internalLink: account?.surveysEnabled === false ? RoutePath.Surveys : RoutePath.SurveysTemplates,
            style: { gridArea: 'area4' },
        },
        {
            id: 5,
            title: t('HomePage.usersTitle'),
            description: t('HomePage.usersDescription'),
            isPassed: accountOnboarding.usersCreated,
            link: ExternalRoutes.LearnMore[i18n.language].users,
            internalLink: RoutePath.AccountSettingsUsers,
            style: { gridArea: 'area5' },
        },
    ];

    const totalSteps = LIST.length;
    const passedNumber = Object.values(accountOnboarding).filter(Boolean).length;
    const percentage = Math.floor((passedNumber * 100) / totalSteps);

    return (
        <Container>
            <Typography variant="subtitle2" align="center" sx={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
                {t('HomePage.getStarted', { steps: totalSteps })}
            </Typography>
            <Collapse in={expanded}>
                <StepsWrapper>
                    {LIST.map(item => (
                        <Card key={item.id} {...item} />
                    ))}
                </StepsWrapper>
            </Collapse>
            <div style={{ display: 'flex', gap: 16, marginBottom: '16px' }}>
                <div
                    style={{
                        backgroundColor: '#E4EAEB',
                        height: '14px',
                        width: 'calc(100% - 36px)',
                        borderRadius: '14px',
                    }}
                >
                    <div
                        style={{
                            height: '100%',
                            borderRadius: '14px',
                            backgroundColor: 'var(--primary-main)',
                            width: `${percentage}%`,
                        }}
                    />
                </div>
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {passedNumber}/{totalSteps}
                </Typography>
            </div>
            <div
                style={{
                    position: 'absolute',
                    height: '32px',
                    backgroundColor: '#fff',
                    left: '50%',
                    transform: 'translateX(-50%)',
                }}
            >
                <Button
                    variant="outlined"
                    sx={{
                        color: 'var(--text-secondary)',
                        width: 'max-content',
                        boxShadow: 'none',
                    }}
                    onClick={onExpandSection}
                    endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                >
                    {t('HomePage.continueOnboarding')}
                </Button>
            </div>
        </Container>
    );
};

export default Onboarding;
